import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, Loader2, KeyRound, ArrowLeft, CheckCircle, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenEmail, setTokenEmail] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setVerifying(false);
                return;
            }

            try {
                const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
                setTokenValid(response.data.valid);
                setTokenEmail(response.data.email);
            } catch (error) {
                setTokenValid(false);
            } finally {
                setVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            await axios.post(`${API_URL}/api/auth/reset-password`, {
                token,
                new_password: password
            });
            setSuccess(true);
            toast.success('Password reset successful!');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to reset password. Please try again.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <div className="min-h-screen flex items-center justify-center gradient-bg">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col gradient-bg">
            {/* Navigation Bar */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                <span className="font-bold text-lg text-white">Autoimmune Pathways</span>
                                <span className="text-xs sm:text-sm text-slate-400">by Me and My Pandas</span>
                            </div>
                        </Link>
                        <Link to="/login">
                            <Button variant="ghost" className="text-slate-300 hover:text-white">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Login
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center p-6 pt-24">
                <Card className="w-full max-w-md border-0 shadow-xl bg-card/80 backdrop-blur-xl">
                    <CardHeader className="text-center space-y-2">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg ${
                            success ? 'bg-green-600' : !token || !tokenValid ? 'bg-red-600' : 'bg-gradient-to-br from-indigo-600 to-violet-600 shadow-primary/30'
                        }`}>
                            {success ? (
                                <CheckCircle className="w-7 h-7 text-white" />
                            ) : !token || !tokenValid ? (
                                <AlertCircle className="w-7 h-7 text-white" />
                            ) : (
                                <KeyRound className="w-7 h-7 text-white" />
                            )}
                        </div>
                        <CardTitle className="font-heading text-2xl">
                            {success ? 'Password Reset!' : !token || !tokenValid ? 'Invalid Link' : 'Reset Password'}
                        </CardTitle>
                        <CardDescription>
                            {success 
                                ? 'Your password has been updated successfully.'
                                : !token 
                                    ? 'No reset token provided.'
                                    : !tokenValid 
                                        ? 'This reset link is invalid or has expired.'
                                        : `Enter a new password for ${tokenEmail}`
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {success ? (
                            <div className="space-y-4">
                                <Button
                                    onClick={() => navigate('/login')}
                                    className="w-full h-11 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-medium shadow-lg shadow-primary/25"
                                >
                                    Go to Login
                                </Button>
                            </div>
                        ) : !token || !tokenValid ? (
                            <div className="space-y-4">
                                <p className="text-sm text-muted-foreground text-center">
                                    Please request a new password reset link.
                                </p>
                                <Button
                                    onClick={() => navigate('/forgot-password')}
                                    className="w-full h-11 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-medium shadow-lg shadow-primary/25"
                                >
                                    Request New Link
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="password">New Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            minLength={8}
                                            className="h-11 pr-10"
                                            data-testid="reset-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <Input
                                        id="confirmPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        minLength={8}
                                        className="h-11"
                                        data-testid="reset-confirm-password"
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Password must be at least 8 characters long.
                                </p>
                                <Button
                                    type="submit"
                                    className="w-full h-11 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-medium shadow-lg shadow-primary/25"
                                    disabled={loading}
                                    data-testid="reset-submit"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Resetting...
                                        </>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
